import React, { useState, useEffect } from 'react';
import { CalendarCheck, Loader2 } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const CAL_COM_URL_REGEX = /^https?:\/\/(www\.)?cal\.com\/[^/]+\/.+/i;

// Lazy-load Cal.com embed script on first interaction (cached via module-level promise)
let calComPromise = null;
function loadCalCom() {
  if (calComPromise) return calComPromise;
  if (window.Cal?.loaded) return Promise.resolve();

  calComPromise = new Promise((resolve, reject) => {
    (function (C, A, L) {
      let p = function (a, ar) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          const script = d.createElement("script");
          script.src = A;
          script.onload = resolve;
          script.onerror = () => { calComPromise = null; reject(new Error('Cal.com script failed')); };
          d.head.appendChild(script);
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", { origin: "https://app.cal.com" });
  });

  return calComPromise;
}

/**
 * CalComRenderer - Render component for cal-com block
 * Defers Cal.com embed script until button click for faster page loads.
 */
const CalComRenderer = ({ block, theme = {} }) => {
  const { content } = block;
  const { calComUrl, buttonText = 'Schedule a Meeting' } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const [loading, setLoading] = useState(false);

  // Inject mobile-friendly CSS overrides for the Cal.com modal
  useEffect(() => {
    const styleId = 'cal-com-mobile-overrides';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media (max-width: 768px) {
        cal-modal-box {
          --cal-modal-top: 0 !important;
        }
        cal-modal-box .modal-box {
          width: 100vw !important;
          max-width: 100vw !important;
          height: 100vh !important;
          max-height: 100vh !important;
          top: 0 !important;
          left: 0 !important;
          transform: none !important;
          margin: 0 !important;
          border-radius: 0 !important;
        }
        cal-modal-box iframe {
          width: 100% !important;
          height: 100% !important;
          min-height: 100vh !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const isValidUrl = CAL_COM_URL_REGEX.test(calComUrl);

  const handleClick = async () => {
    if (!isValidUrl) return;

    setLoading(true);
    try {
      await loadCalCom();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const url = new URL(calComUrl);
      const calLink = url.pathname.replace(/^\//, '');
      window.Cal("modal", { calLink, layout: "month_view" });
    } catch {
      window.open(calComUrl, '_blank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className={`max-w-4xl mx-auto flex flex-col ${styles.contentAlignmentClass}`}>
        {isValidUrl ? (
          <button
            onClick={handleClick}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all hover:opacity-90 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg min-h-[48px] sm:min-h-[52px]"
            style={styles.getButtonStyles()}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : <CalendarCheck size={20} />}
            {loading ? 'Loading...' : buttonText}
          </button>
        ) : (
          <p className="text-gray-400 italic text-sm">
            Enter a Cal.com URL to show the booking button
          </p>
        )}
      </div>
    </div>
  );
};

export default CalComRenderer;
